import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import "../../emailBuilderCompactPopupActions.scss";

type TEmailBuilderApplyTemplateConfirmModalProps = {
  open: boolean;
  confirming: boolean;
  templateName: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export function EmailBuilderApplyTemplateConfirmModal(
  props: TEmailBuilderApplyTemplateConfirmModalProps,
) {
  const { t } = useTranslation();
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || props.confirming) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, props.confirming]);

  function handleClose() {
    if (props.confirming) {
      return;
    }

    props.onClose();
  }

  return (
    <Portal isMounted={props.open} parentId="portal-root">
      <div className="email-builder-compact-popup">
        <Popup
          open={props.open}
          type="warning"
          title={t("Apply this template?")}
          description={
            props.templateName != null && props.templateName.length > 0
              ? t(
                  'Applying "{{name}}" replaces your current design. Changes will auto-save. You can undo afterward with Undo.',
                  { name: props.templateName },
                )
              : t(
                  "Applying this template replaces your current design. Changes will auto-save. You can undo afterward with Undo.",
                )
          }
          closable
          onCancel={handleClose}
          cancelText={t("Cancel")}
          okText={props.confirming ? t("Applying…") : t("Apply template")}
          onOk={props.onConfirm}
          okDisabled={props.confirming}
          cancelDisabled={props.confirming}
          backDropCancel={!props.confirming}
          compactActions
        />
      </div>
    </Portal>
  );
}
